import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, Reveal, SectionHeading, LeafSprig } from "@/components/Motion";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CONTACT } from "@/data/content";

const ContactHero = () => (
  <section className="relative overflow-hidden bg-paper pb-20 pt-36 text-ink" data-testid="contact-hero">
    <LeafSprig className="pointer-events-none absolute -right-8 top-28 h-72 rotate-[160deg] text-leaf/25" />
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <img
        src="/images/logo-duo.png"
        alt="Root2Shoot — Sustainable Café and Film & TV Catering"
        className="mb-9 h-14 w-auto sm:h-16"
        data-testid="contact-logo"
      />
      <p className="label-mono-ink flex items-center gap-4">
        Contact
        <span className="h-px w-20 bg-gold-deep/60" aria-hidden="true" />
      </p>
      <MaskedLines
        lines={["Talk to the"]}
        className="mt-6 font-editorial text-5xl leading-[1.0] tracking-tight text-ink sm:text-6xl lg:text-7xl"
      />
      <MaskedLines
        as="p"
        lines={["kitchen"]}
        className="font-display text-4xl italic leading-[1.05] tracking-tight text-gold-deep sm:text-5xl lg:text-6xl"
        delay={0.4}
      />
      <Reveal delay={0.6}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
          Whether it's a production to feed or a question for the café — it all lands with
          the same small team, and a real human replies.
        </p>
      </Reveal>
    </div>
  </section>
);

const Routes = () => (
  <section className="bg-paper pb-24 text-ink" data-testid="contact-routes">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-14 md:grid-cols-2 md:gap-20">
        <Reveal>
          <div className="border-t-2 border-gold-deep/60 pt-9" data-testid="contact-route-filmtv">
            <p className="label-mono-ink">01 — Productions</p>
            <h3 className="mt-5 font-editorial text-3xl tracking-tight text-ink sm:text-4xl">Film &amp; TV enquiries</h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">
              Productions, packages and hotbox delivery — use the catering enquiry form and we'll
              come back with a tailored menu and quote, usually within one working day.
            </p>
            <Link to="/catering#enquiry" className="btn-ink mt-9" data-testid="contact-cta-filmtv">
              Go to Catering Enquiries <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="border-t-2 border-gold-deep/60 pt-9" data-testid="contact-route-cafe">
            <p className="label-mono-ink">02 — The Café</p>
            <h3 className="mt-5 font-editorial text-3xl tracking-tight text-ink sm:text-4xl">Café enquiries</h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">
              Questions about the café, the menu, private hires or anything else — use the form
              below or email us directly.
            </p>
            <a href="#cafe-enquiries" className="btn-ghost-ink mt-9" data-testid="contact-cta-cafe">
              Café Enquiry Form <ArrowRight size={16} />
            </a>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="mt-20 grid gap-10 border-y border-gold-deep/40 py-12 sm:grid-cols-2 lg:grid-cols-4" data-testid="contact-details">
          <div>
            <p className="label-mono-ink">Email</p>
            <a href={`mailto:${CONTACT.email}`} className="mt-4 block text-base text-gold-deep underline-offset-4 hover:underline" data-testid="contact-email">
              {CONTACT.email}
            </a>
          </div>
          <div>
            <p className="label-mono-ink">Phone</p>
            <a href={CONTACT.phoneHref} className="mt-4 block text-base text-ink transition-colors hover:text-gold-deep" data-testid="contact-phone">
              {CONTACT.phoneDisplay}
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="mt-2 block text-sm text-ink/60 underline-offset-4 transition-colors hover:text-gold-deep hover:underline" data-testid="contact-whatsapp">
              Also on WhatsApp
            </a>
          </div>
          <div>
            <p className="label-mono-ink">The Café</p>
            <p className="mt-4 text-sm leading-relaxed text-ink/70">
              {CONTACT.addressLines[0]}<br />{CONTACT.addressLines[1]}, {CONTACT.postcode}
            </p>
          </div>
          <div>
            <p className="label-mono-ink">Opening Hours</p>
            <p className="mt-4 text-sm leading-relaxed text-ink/70">
              {CONTACT.hoursWeekday}<br />{CONTACT.hoursWeekend}
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CafeForm = () => (
  <section id="cafe-enquiries" className="scroll-mt-28 bg-noir py-24 sm:py-32" data-testid="cafe-enquiries-section">
    <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeading
          kicker="Café & General Enquiries"
          title={<>Drop us <span className="text-gold">a line</span></>}
        />
        <Reveal delay={0.1}>
          <p className="mt-7 text-base leading-relaxed text-sage">
            For production catering, use the{" "}
            <Link to="/catering#enquiry" className="text-gold underline-offset-4 hover:underline" data-testid="contact-form-catering-link">
              catering enquiry form
            </Link>{" "}
            so we get the details we need to quote.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.12} className="lg:col-span-7">
        <div className="border border-gold/25 bg-obsidian/70 p-7 sm:p-12">
          <p className="label-mono mb-10">Your Message</p>
          <EnquiryForm variant="general" />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Root2Shoot — Film & TV Catering & Café Enquiries"
        siteName="Root2Shoot"
        description="Start a film & TV production catering enquiry or contact the Root2Shoot sustainable café in Stockport. Email info@root2shoot.com — Studio House, Battersea Road, SK4 3EA."
        image="/images/logo-duo.png"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
        jsonLd={{ "@context": "https://schema.org", "@type": "ContactPage", name: "Contact Root2Shoot" }}
      />
      <ContactHero />
      <Routes />
      <CafeForm />
    </>
  );
}
